import { useState, useRef } from "react";
import { Camera, ImagePlus, X, FileWarning } from "lucide-react";

const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif";
const MAX_FILES = 10;
const MAX_SIZE_MB = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

export default function PhotoPicker({ photos, setPhotos, disabled = false }) {
  const inputRef = useRef(null);
  const [error, setError] = useState("");

  const addFiles = (incoming) => {
    setError("");
    const files = Array.from(incoming || []);
    if (!files.length) return;

    const remaining = MAX_FILES - photos.length;
    const next = [];
    for (const file of files.slice(0, remaining)) {
      const type = (file.type || "").toLowerCase();
      const nameLower = (file.name || "").toLowerCase();
      const isHeic = nameLower.endsWith(".heic") || nameLower.endsWith(".heif") || type.includes("heic") || type.includes("heif");
      const isSupported = ["image/jpeg", "image/png", "image/webp"].includes(type) || isHeic;
      if (!isSupported) {
        setError(`Format non supporté : ${file.name}`);
        continue;
      }
      if (file.size > MAX_SIZE_BYTES) {
        setError(`"${file.name}" dépasse ${MAX_SIZE_MB} Mo.`);
        continue;
      }
      const preview = isHeic ? null : URL.createObjectURL(file);
      next.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        name: file.name,
        size: file.size,
        type,
        isHeic,
        preview,
      });
    }
    if (files.length > remaining) {
      setError(`Maximum ${MAX_FILES} photos. ${files.length - remaining} fichier(s) ignoré(s).`);
    }
    if (next.length) setPhotos([...photos, ...next]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (id) => {
    const p = photos.find((x) => x.id === id);
    if (p?.preview) URL.revokeObjectURL(p.preview);
    setPhotos(photos.filter((x) => x.id !== id));
  };

  const pickGallery = () => inputRef.current?.click();
  const pickCamera = () => {
    if (!inputRef.current) return;
    inputRef.current.setAttribute("capture", "environment");
    inputRef.current.click();
    // Reset capture so gallery picker works next time
    setTimeout(() => inputRef.current?.removeAttribute("capture"), 300);
  };

  return (
    <div data-testid="photo-picker" className="rounded-2xl border border-black/10 bg-white p-6 md:p-8">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#FF2800] font-semibold">
            📷 Photos de vos meubles et objets
          </div>
          <div className="text-sm text-neutral-600 mt-2 leading-relaxed max-w-xl">
            Ajoutez des photos pour nous aider à évaluer le volume et préparer votre devis.
          </div>
        </div>
        {photos.length > 0 && (
          <div className="shrink-0 text-xs uppercase tracking-[0.25em] text-neutral-500 font-medium whitespace-nowrap">
            {photos.length} / {MAX_FILES}
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        data-testid="photo-input"
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        onChange={(e) => addFiles(e.target.files)}
        disabled={disabled || photos.length >= MAX_FILES}
      />

      {/* Previews grid */}
      {photos.length > 0 && (
        <div className="mt-6 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
          {photos.map((p) => (
            <div
              key={p.id}
              data-testid={`photo-preview-${p.id}`}
              className="relative aspect-square rounded-xl overflow-hidden border border-black/10 bg-[#FAFAFA] group"
            >
              {p.preview ? (
                <img src={p.preview} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center">
                  <FileWarning size={20} className="text-[#FFB800] mb-1" strokeWidth={1.5} />
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500">HEIC</div>
                  <div className="text-[10px] text-neutral-400 truncate max-w-full">{p.name}</div>
                </div>
              )}
              <button
                type="button"
                data-testid={`photo-remove-${p.id}`}
                onClick={() => remove(p.id)}
                aria-label={`Retirer ${p.name}`}
                className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-[#FF2800] transition"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      {photos.length < MAX_FILES && (
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            data-testid="photo-pick-gallery"
            onClick={pickGallery}
            disabled={disabled}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-black border border-black/15 hover:bg-black hover:text-white transition disabled:opacity-50"
          >
            <ImagePlus size={16} />
            Choisir depuis la galerie
          </button>
          <button
            type="button"
            data-testid="photo-pick-camera"
            onClick={pickCamera}
            disabled={disabled}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-black border border-black/15 hover:bg-black hover:text-white transition disabled:opacity-50"
          >
            <Camera size={16} />
            Prendre une photo
          </button>
        </div>
      )}

      {error && (
        <div data-testid="photo-error" className="mt-4 text-sm text-[#FF2800]">
          {error}
        </div>
      )}

      <div className="mt-5 text-[11px] uppercase tracking-[0.18em] text-neutral-400">
        Maximum {MAX_FILES} photos · JPEG, PNG, WebP, HEIC · 10 Mo par photo · Facultatif
      </div>
    </div>
  );
}
